<template>
	<BaseFormField
		ref="formFieldComponentRef"
		class="base-form-field"
		:disabled="disabled"
		v-model="selectedSchool"
		:schema="schoolSelectSchema"
		:autocompleteOptions="filteredSchools"
		@update:modelValue="selectSchool"
	/>
</template>

<script setup lang="ts">
	import '@/assets/css/scrollbar.css';
	import { ref, onMounted, watch } from 'vue';
	import axios from 'axios';
	import { FieldSchema } from '@/models/form';
	import { School } from '@/models/loans';
	import BaseFormField from '@/components/form-fields/BaseFormField.vue';

	const props = defineProps<{
		disabled: boolean;
		initialSchool?: School;
		programTypeId: number;
		productTypeId: number;
		lenderId: number;
	}>();

	const schoolSelectSchema: FieldSchema = {
		name: 'school',
		type: 'autocomplete',
		autocompleteProperty: 'schoolName',
		autoCompleteResultsErrorMessage: 'No matches found',
		hideLabel: false,
		label: 'School',
		placeholder: 'Search...',
		required: false
	};

	const selectedSchool = ref({} as School);
	const filteredSchools = ref<School[]>([]);
	const formFieldComponentRef = ref<InstanceType<typeof BaseFormField> | null>(null);

	const emit = defineEmits(['update:selectedSchool']);
	
	watch(selectedSchool, (newValue) => {
		emit('update:selectedSchool', newValue);
	});

	function selectSchool(school: School) {
		selectedSchool.value = school;

		if (formFieldComponentRef.value) {
			const validSchoolSelection =
				selectedSchool.value &&
				Object.keys(selectedSchool.value).includes("schoolDOEId") &&
				Object.keys(selectedSchool.value).includes("schoolBranchDOEId");

			if (validSchoolSelection) {
				formFieldComponentRef.value?.autocompleteInputRef?.setAttribute(
					"title",
					`${school.schoolDOEId}-${school.schoolBranchDOEId}`
				);
			} else {
				formFieldComponentRef.value?.autocompleteInputRef?.removeAttribute("title");
			}
		}
	}

	function findSchool(schoolCode: string, schoolBranch: string) {
		return filteredSchools.value.find((s: School) => s.schoolDOEId === schoolCode && s.schoolBranchDOEId === schoolBranch);
	}

	function findSchoolWithDetailId(schoolDetailsId: number) {
		return filteredSchools.value.find((s: School) => s.schoolDetailsId == schoolDetailsId);
	}
	
	function discardChanges(initialValue: School) {
		const matchingSchool = filteredSchools.value.find((school) => {
			const matchingCode = school.schoolDOEId === initialValue?.schoolDOEId;
			const matchingBranch = school.schoolBranchDOEId === initialValue?.schoolBranchDOEId;

			return matchingCode && matchingBranch;
		});

		if (matchingSchool) {
			selectSchool(matchingSchool);
		}
	}

	async function getSchoolsAsync() {
		try {
			const response = await axios.get('/api/schools', {
				params: {
					programTypeId: props.programTypeId,
					productTypeId: props.productTypeId,
					lenderId: props.lenderId
				}
			});

			filteredSchools.value = response.data;

			if (props.initialSchool) {
				const matchingSchool =
					(props.initialSchool.schoolDetailsId
						? filteredSchools.value.find((school) => school.schoolDetailsId == props.initialSchool?.schoolDetailsId)
						: undefined)
					?? filteredSchools.value.find((school) =>
						school.schoolDOEId === props.initialSchool?.schoolDOEId
						&& school.schoolBranchDOEId === props.initialSchool?.schoolBranchDOEId);

				if (matchingSchool) {
					selectSchool(matchingSchool);
				}
			}
		} catch (error) {
			console.error('Failed to get schools:', error);
		}
	}

	onMounted(async () => {
		await getSchoolsAsync();
	});

	defineExpose({
		filteredSchools,
		selectedSchool,
		findSchool,
		findSchoolWithDetailId,
		discardChanges
	});
</script>

<style scoped>
	.base-form-field {
		margin-bottom: 0px !important;
	}

	::v-deep(input) {
		border-radius: 4px;
		border: 1px solid #ccc;
		min-height: 46px;
		font-size: 14px;
	}

	::v-deep(ul) {
		border: none;
		border-radius: 4px;
	}

	::v-deep(li) {
		font-size: 14px;
		padding: 7px 12px !important;
	}

	::v-deep(input:disabled) {
		border: none;
		background-color: #ddd;
		opacity: 0.7;
	}

	::v-deep(label) {
		display: none !important;
	}

	::v-deep(.results-error) {
		padding-top: 4px;
		color: #dc3545 !important;
		font-style: italic;
		font-size: 14px;
	}

	::v-deep(ul::-webkit-scrollbar) {
		width: 10px;
		background-color: transparent;
	}

	::v-deep(ul::-webkit-scrollbar-thumb) {
		background-color: black;
		border-radius: 5px;
	}

	::v-deep(ul::-webkit-scrollbar-thumb:hover) {
		background-color: #333; /* darker on hover */
	}

	::v-deep(ul::-webkit-scrollbar-track) {
		background-color: transparent;
		border-radius: 5px;
	}
</style>
